import { TrackSkeleton } from "../track-skeleton";
import { useBufferController } from "@/services/buffer-controller";
import { lineConfig, pointNames } from "@/shared/consts";
import { useTimelinePlayback } from "@/contexts/playback-context";
import { observer, useSelector } from "@legendapp/state/react";
import { useMemo } from "react";
import { Annotation } from "@/components/annotation";
import { Point, Points } from "@react-three/drei";
import { getShoulderMidpoint } from "../utils";
import { useViewSettings } from "@/view-settings";
import {
  highlightLastDetections,
  useHighlightCamera,
} from "./useHighlightCamera";
import {
  hiddenTracks$,
  selectedTrack$,
  selectedTrackPosition$,
  visibleTracksSnapshots$,
} from "../state";
import { MAP_CONTROLS_DEFAULT_OPTIONS, mapControlsOptions$ } from "../controls";
import { useThree } from "@react-three/fiber";
import { Vector3 } from "three";
import { TrackID } from "@/shared/types";
import { batch } from "@legendapp/state";
import { AnimalAvatar } from "@/components/animal-avatar";
import { animalAvatarGenerator } from "@/shared/lib/animal-avatar";

export const TrackSnapshot = observer(
  ({ trackId, debug }: { trackId: TrackID; debug?: boolean }) => {
    const [tracksBuffer] = useBufferController();
    const timelinePlayback = useTimelinePlayback();
    const currentTime = timelinePlayback.currentTime.get();
    const snapshot = tracksBuffer.getSnapshotToDraw(trackId, currentTime);
    const animalAvatar = useMemo(
      () => animalAvatarGenerator.getRandomAvatar(trackId),
      [trackId]
    );
    const viewSettings = useViewSettings();
    const confidence = viewSettings.trackConfidence.get();
    const showTrackAvatar = viewSettings.showTrackAvatar.get();
    const showTrackID = viewSettings.showTrackId.get();
    const { camera } = useThree();

    useHighlightCamera(snapshot, trackId);

    const shoulderMidpoint = useMemo(
      () => getShoulderMidpoint(snapshot),
      [snapshot]
    );

    const isSelected = useSelector(() => selectedTrack$.get() === trackId);

    if (isSelected) {
      selectedTrackPosition$.set(shoulderMidpoint);
    }

    const onTrackSelected = () => {
      if (!isSelected) {
        selectedTrack$.set(trackId);

        const newTarget = new Vector3()
          .fromArray(shoulderMidpoint)
          .addScalar(1);
        const newMaxDistance = camera.position.distanceTo(newTarget);

        mapControlsOptions$.maxDistance.set(newMaxDistance);
      } else {
        batch(() => {
          selectedTrack$.set(null);
          selectedTrackPosition$.set(null);
          mapControlsOptions$.set({ ...MAP_CONTROLS_DEFAULT_OPTIONS });
        });
      }

      highlightLastDetections(
        visibleTracksSnapshots$.peek(),
        hiddenTracks$.peek()
      );
    };

    if (!snapshot) return null;

    return (
      <>
        <group position={shoulderMidpoint}>
          <Annotation
            wrapperClassName="pointer-events-auto"
            position={[0, 0, 0]}
            zIndexRange={[100, 0]}
          >
            <figure className="min-w-[50px] min-h-[50px]">
              {showTrackAvatar && (
                <AnimalAvatar
                  value={animalAvatar}
                  hoverable
                  trackId={trackId}
                  size="50px"
                  selected={isSelected}
                  onClick={onTrackSelected}
                />
              )}
            </figure>
            {showTrackID && (
              <span className="text-base whitespace-nowrap font-bold pointer-events-none">
                Track: {trackId}
              </span>
            )}
          </Annotation>
        </group>
        {debug && (
          <Points limit={2} range={2}>
            <pointsMaterial
              color="red"
              size={0.1}
              vertexColors
              sizeAttenuation
            />
            <Point
              position={[
                shoulderMidpoint[0],
                shoulderMidpoint[1] - 0.5,
                shoulderMidpoint[2],
              ]}
            />
          </Points>
        )}
        <TrackSkeleton
          color={animalAvatar?.colorHex || "#ff0000"}
          lines={lineConfig}
          track={snapshot}
          confidence={Number(confidence)}
          pointPopupContent={(selected) => (
            <ul>
              <li className="whitespace-nowrap">
                Name: {pointNames[selected]}
              </li>
              <li className="whitespace-nowrap">
                X: {snapshot.pose_x[selected]}
              </li>
              <li className="whitespace-nowrap">
                Y: {snapshot.pose_y[selected]}
              </li>
              <li className="whitespace-nowrap">
                Z: {snapshot.pose_z[selected]}
              </li>
              <li className="whitespace-nowrap">
                Conf: {snapshot.pose_conf[selected]}
              </li>
              <li className="whitespace-nowrap">
                Datetime: {snapshot.datetime}
              </li>
            </ul>
          )}
        />
      </>
    );
  }
);
